import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useIntersectionObserver } from '../hooks/useIntersectionObserver';
import { useSpotlight } from '../hooks/useSpotlight';
import { useI18n } from '../i18n/I18nContext';

interface StarModelCardProps {
  title: string;
  status: string;
  description: string;
  details?: string;
  link?: string;
}

function StarModelCard({ title, status, description, details, link }: StarModelCardProps) {
  const { t } = useI18n();
  const [isVisible, setIsVisible] = useState(false);

  const ref = useIntersectionObserver({
    onIntersect: () => setIsVisible(true),
    threshold: 0.05,
    rootMargin: '0px 0px -20px 0px',
  });
  const handleSpotlight = useSpotlight<HTMLDivElement>();

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      onPointerMove={handleSpotlight}
      className={`spotlight-card bg-black text-white p-8 rounded-lg relative overflow-hidden group border border-zinc-800 hover:border-[#FDCB12] focus-within:border-[#FDCB12] transition-all duration-700 ease-out ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
    >
      <div className="absolute inset-0 bg-gradient-to-r from-[#FDCB12] to-[#FF5500] opacity-0 group-hover:opacity-10 transition-opacity" />
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold">{title}</h3>
          <span className={`px-3 py-1 rounded-full text-sm ${
            status === 'Active' || status === 'Activo' || status === 'Attivo' || status === 'Активна' || status === 'Ativo' || status === 'Aktiv' ? 'bg-green-500' : 'bg-yellow-500'
          }`}>
            {status}
          </span>
        </div>
        <p className="text-gray-300 mb-4">{description}</p>
        {details && (
          <div className="mb-4 max-h-0 overflow-hidden rounded-md border border-transparent bg-zinc-900/60 p-0 opacity-0 transition-all duration-300 group-hover:max-h-40 group-hover:border-zinc-700 group-hover:p-4 group-hover:opacity-100 group-focus-within:max-h-40 group-focus-within:border-zinc-700 group-focus-within:p-4 group-focus-within:opacity-100">
            <p className="text-sm text-gray-200">{details}</p>
          </div>
        )}
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="text-[#FDCB12] font-medium flex items-center hover:text-[#FF5500] transition-all duration-300 group"
          >
            {t.starModel.visitWebsite} <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          </a>
        )}
      </div>
    </div>
  );
}

export default StarModelCard;
